import React, { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Send, Sparkles, TrendingUp, AlertTriangle, BarChart3 } from 'lucide-react';
import { aiApi } from '../services/api';
import { Card, SectionHeader, Badge } from '../components/ui';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line,
} from 'recharts';
import toast from 'react-hot-toast';

const EXAMPLE_QUERIES = [
  'What are my top cost drivers?',
  'Show underutilized resources',
  'How much can I save on compute?',
  "What's my projected cloud spend for next quarter?",
  'Which databases have the most slow queries?',
  'How can I reduce my carbon footprint by 20%?',
];

const TABS = ['Chat', 'Capacity Planning'];

// Mock capacity forecast data
const MOCK_FORECAST = [
  { month: 'Jan', actual: 62, forecast: 62, upper: 65, lower: 59 },
  { month: 'Feb', actual: 65, forecast: 65, upper: 69, lower: 61 },
  { month: 'Mar', actual: 68, forecast: 68, upper: 73, lower: 63 },
  { month: 'Apr', actual: null, forecast: 72, upper: 79, lower: 65 },
  { month: 'May', actual: null, forecast: 76, upper: 85, lower: 67 },
  { month: 'Jun', actual: null, forecast: 80, upper: 91, lower: 69 },
  { month: 'Jul', actual: null, forecast: 83, upper: 96, lower: 70 },
  { month: 'Aug', actual: null, forecast: 85, upper: 100, lower: 70 },
];

const MOCK_RESOURCE_FORECAST = [
  { resource: 'Compute (vCPUs)', current: 256, projected3m: 310, projected6m: 380, status: 'Warning' },
  { resource: 'Memory (GB)', current: 1024, projected3m: 1200, projected6m: 1450, status: 'OK' },
  { resource: 'Storage (TB)', current: 48, projected3m: 56, projected6m: 68, status: 'OK' },
  { resource: 'Network (Gbps)', current: 10, projected3m: 12, projected6m: 15, status: 'Warning' },
  { resource: 'DB Connections', current: 420, projected3m: 520, projected6m: 640, status: 'Critical' },
];

// Mock anomaly alerts
const MOCK_ANOMALIES = [
  { id: 1, message: 'Unusual CPU spike on prod-api-03', severity: 'WARNING', time: '12 min ago' },
  { id: 2, message: 'Memory leak detected in payment-svc', severity: 'CRITICAL', time: '28 min ago' },
  { id: 3, message: 'Abnormal network traffic pattern', severity: 'WARNING', time: '1h ago' },
  { id: 4, message: 'Cost anomaly: 40% above baseline', severity: 'INFO', time: '2h ago' },
];

const tooltipStyle = {
  background: '#0d1527',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
};

const tableHeaderStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  color: 'rgba(255,255,255,0.35)',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

const SEVERITY_COLORS = {
  CRITICAL: '#ff5c5c',
  WARNING: '#ffb800',
  INFO: '#4a9eff',
};

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  let displayText = msg.content;
  let tableData = null;
  let chartData = null;

  if (!isUser) {
    try {
      const parsed = JSON.parse(msg.content);
      displayText = parsed.answer || parsed.message || msg.content;
      if (parsed.dataType === 'table' && Array.isArray(parsed.data)) {
        tableData = parsed.data;
      }
      if (parsed.dataType === 'chart' && Array.isArray(parsed.data)) {
        chartData = parsed.data;
      }
    } catch {
      // plain text response
    }
  }

  return (
    <div style={{
      display: 'flex',
      gap: 12,
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 16,
    }}>
      {/* AI Avatar */}
      {!isUser && (
        <div style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          flexShrink: 0,
          background: 'linear-gradient(135deg, #0084ff, #00c896)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Sparkles size={16} color="white" />
        </div>
      )}

      <div style={{ maxWidth: '72%' }}>
        {/* Message */}
        <div style={{
          padding: '12px 16px',
          borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
          background: isUser ? 'rgba(0,132,255,0.18)' : 'rgba(255,255,255,0.05)',
          border: `1px solid ${isUser ? 'rgba(0,132,255,0.3)' : 'rgba(255,255,255,0.08)'}`,
          color: 'rgba(255,255,255,0.85)',
          fontSize: 14,
          lineHeight: 1.6,
        }}>
          {displayText}
        </div>

        {/* Table Data */}
        {tableData && tableData.length > 0 && (
          <div style={{ marginTop: 8, overflowX: 'auto' }}>
            <table style={{
              borderCollapse: 'collapse',
              fontSize: 12,
              background: 'rgba(255,255,255,0.03)',
              borderRadius: 8,
              overflow: 'hidden',
              width: '100%',
            }}>
              <thead>
                <tr>
                  {Object.keys(tableData[0]).map((k) => (
                    <th key={k} style={{
                      padding: '8px 12px',
                      textAlign: 'left',
                      color: 'rgba(255,255,255,0.4)',
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                      textTransform: 'capitalize',
                      fontWeight: 600,
                    }}>
                      {k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, i) => (
                  <tr key={i}>
                    {Object.values(row).map((v, j) => (
                      <td key={j} style={{
                        padding: '8px 12px',
                        color: 'rgba(255,255,255,0.7)',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                      }}>
                        {String(v)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Inline Chart Data */}
        {chartData && chartData.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey={Object.keys(chartData[0])[0]} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey={Object.keys(chartData[0])[1]} stroke="#0084ff" fill="rgba(0,132,255,0.15)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Timestamp */}
        <div style={{
          color: 'rgba(255,255,255,0.2)',
          fontSize: 10,
          marginTop: 4,
          textAlign: isUser ? 'right' : 'left',
        }}>
          {new Date(msg.ts).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
    </div>
  );
}

export default function AiQueryPage() {
  const [input, setInput] = useState('');
  const [activeTab, setActiveTab] = useState('Chat');
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: JSON.stringify({
        answer:
          "Hello! I'm SmalBlu's AI assistant. I can help you understand your infrastructure costs, identify savings opportunities, and query your monitoring data in plain English. What would you like to know?",
      }),
      ts: Date.now(),
    },
  ]);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const mutation = useMutation({
    mutationFn: ({ query }) => aiApi.query(query, 'demo-user'),
    onSuccess: (res) => {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.data.response
            ? JSON.stringify({ answer: res.data.response })
            : res.data.aiResponse || '{"answer": "I processed your request."}',
          ts: Date.now(),
        },
      ]);
    },
    onError: () => {
      toast.error('AI query failed. Check your API configuration.');
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: JSON.stringify({
            answer:
              'Sorry, I encountered an error processing your query. Please try again or check the API configuration.',
          }),
          ts: Date.now(),
        },
      ]);
    },
  });

  const handleSend = (queryText) => {
    const text = (queryText || input).trim();
    if (!text) return;
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: text, ts: Date.now() },
    ]);
    setInput('');
    mutation.mutate({ query: text });
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}>
      <SectionHeader
        title="AI Assistant"
        subtitle="Query your infrastructure data in plain English"
      />

      {/* Tab Selector */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 20px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid #0084ff' : '2px solid transparent',
              background: 'transparent',
              color: activeTab === tab ? '#fff' : 'rgba(255,255,255,0.4)',
              transition: 'all 0.15s ease',
              fontFamily: 'inherit',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'Chat' && (
        <div style={{
          display: 'flex',
          gap: 20,
          flex: 1,
          minHeight: 0,
        }}>
          {/* Chat Area */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}>
            {/* Messages */}
            <Card style={{
              flex: 1,
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              marginBottom: 12,
            }}>
              <div style={{
                flex: 1,
                overflowY: 'auto',
                paddingRight: 6,
              }}>
                {messages.map((msg, i) => (
                  <MessageBubble key={i} msg={msg} />
                ))}

                {/* Typing indicator */}
                {mutation.isPending && (
                  <div style={{
                    display: 'flex',
                    gap: 12,
                    marginBottom: 16,
                  }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, #0084ff, #00c896)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Sparkles size={16} color="white" />
                    </div>
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: '14px 14px 14px 4px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: 'rgba(255,255,255,0.4)',
                      fontSize: 14,
                    }}>
                      Analyzing your infrastructure data...
                    </div>
                  </div>
                )}

                <div ref={bottomRef} />
              </div>
            </Card>

            {/* Input Bar */}
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Ask about your infrastructure... e.g. 'Show my top 5 cost drivers'"
                style={{
                  flex: 1,
                  padding: '13px 18px',
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff',
                  fontSize: 14,
                  outline: 'none',
                  fontFamily: 'inherit',
                  transition: 'border-color 0.15s',
                }}
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || mutation.isPending}
                style={{
                  padding: '13px 20px',
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #0084ff, #0066cc)',
                  border: 'none',
                  color: 'white',
                  cursor: !input.trim() || mutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: !input.trim() || mutation.isPending ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'opacity 0.15s',
                }}
              >
                <Send size={18} />
              </button>
            </div>
          </div>

          {/* Sidebar: Examples + Anomalies */}
          <div style={{ width: 250, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Example Queries */}
            <Card>
              <div style={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: 14,
              }}>
                Example Queries
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}>
                {EXAMPLE_QUERIES.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(q)}
                    style={{
                      padding: '9px 11px',
                      borderRadius: 8,
                      textAlign: 'left',
                      fontSize: 12,
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.07)',
                      color: 'rgba(255,255,255,0.55)',
                      cursor: 'pointer',
                      lineHeight: 1.4,
                      transition: 'all 0.15s',
                      fontFamily: 'inherit',
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </Card>

            {/* Anomaly Detection Alerts */}
            <Card>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14,
              }}>
                <AlertTriangle size={13} color="#ffb800" />
                <span style={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}>
                  Anomaly Alerts
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {MOCK_ANOMALIES.map((a) => (
                  <div key={a.id} style={{
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: SEVERITY_COLORS[a.severity],
                      }} />
                      <span style={{ color: SEVERITY_COLORS[a.severity], fontSize: 10, fontWeight: 600 }}>
                        {a.severity}
                      </span>
                      <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10, marginLeft: 'auto' }}>
                        {a.time}
                      </span>
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, lineHeight: 1.4 }}>
                      {a.message}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'Capacity Planning' && (
        <div>
          {/* Forecast Chart with Confidence Bounds */}
          <Card style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>Resource Utilization Forecast</div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 2 }}>6-month projection with confidence bounds (shaded area)</div>
              </div>
              <Badge label="AI Forecast" type="PENDING" />
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={MOCK_FORECAST}>
                <defs>
                  <linearGradient id="gradBounds" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0084ff" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#0084ff" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gradForecast" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00c896" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#00c896" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 110]} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} unit="%" />
                <Tooltip contentStyle={tooltipStyle} />
                {/* Upper bound (shaded) */}
                <Area type="monotone" dataKey="upper" stroke="transparent" fill="url(#gradBounds)" name="Upper Bound" />
                {/* Lower bound (clear below) */}
                <Area type="monotone" dataKey="lower" stroke="transparent" fill="#0a0f1e" name="Lower Bound" />
                {/* Forecast line */}
                <Line type="monotone" dataKey="forecast" stroke="#0084ff" strokeWidth={2} strokeDasharray="6 3" dot={false} name="Forecast" />
                {/* Actual line */}
                <Line type="monotone" dataKey="actual" stroke="#00c896" strokeWidth={2.5} dot={{ fill: '#00c896', r: 4 }} name="Actual" connectNulls={false} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          {/* Resource Capacity Table */}
          <Card>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 16, marginBottom: 18 }}>
              Resource Capacity Projections
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Resource', 'Current', '3-Month', '6-Month', 'Status'].map((h) => (
                      <th key={h} style={tableHeaderStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MOCK_RESOURCE_FORECAST.map((r, i) => {
                    const statusColor = r.status === 'Critical' ? '#ff5c5c' : r.status === 'Warning' ? '#ffb800' : '#00c896';
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ padding: '12px', color: '#fff', fontSize: 13, fontWeight: 500 }}>{r.resource}</td>
                        <td style={{ padding: '12px', color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>{r.current.toLocaleString()}</td>
                        <td style={{ padding: '12px', color: '#4a9eff', fontSize: 13, fontWeight: 600 }}>{r.projected3m.toLocaleString()}</td>
                        <td style={{ padding: '12px', color: '#0084ff', fontSize: 13, fontWeight: 600 }}>{r.projected6m.toLocaleString()}</td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                            color: statusColor,
                            background: `${statusColor}18`,
                            border: `1px solid ${statusColor}30`,
                          }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor }} />
                            {r.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
